import { JSX } from "react";

import { Typography } from "@mui/material";
import { ConfigurationValue } from "@plansoft-configuration-renderer/configuration-api";

import { formatNumber } from "@/tools/helper";

import { DisplaySubstructureValueComponent } from "./DisplaySubstructureValueComponent";
import { DisplayTableValueComponent } from "./DisplayTableValueComponent";

interface AccordionDisplayValueProps {
  selectedValue: ConfigurationValue | null;
  displayColumnName: string | undefined;
  tableId: string | null | undefined;
  isSubstructure: boolean;
  displayedValueAttributeId?: string;
  dataType: string;
  unitSymbol: string;
  culture?: string;
  displayType?: string;
  path?: string;
}

interface DisplayValueConfig {
  dataType: string;
  value: unknown;
  unitSymbol: string;
  culture?: string;
}

function getAttributeDisplayValue({
  dataType,
  value,
  unitSymbol,
  culture,
}: DisplayValueConfig): string {
  if (value === undefined) return "";

  if (dataType === "Integer" || dataType === "Double") {
    return `${formatNumber(String(value), culture)} ${unitSymbol}`.trim();
  }

  return `${String(value)} ${unitSymbol}`;
}

interface DisplayTypeFormatterContext {
  formattedValue: string;
  typeCodeFragment: string;
}

// Registry of accordion display formats. Add a new entry here to support
// another `custom.displayType` value from the layout editor - the "value
// only" fallback below covers any undefined or unrecognized displayType.
const DISPLAY_TYPE_FORMATTERS: Record<
  string,
  (context: DisplayTypeFormatterContext) => string
> = {
  Multiple: ({ formattedValue, typeCodeFragment }) =>
    typeCodeFragment
      ? `${typeCodeFragment}: ${formattedValue}`
      : formattedValue,
  TypeCode: ({ formattedValue, typeCodeFragment }) =>
    typeCodeFragment || formattedValue,
};

function getDisplayValue(
  displayType: string | undefined,
  context: DisplayTypeFormatterContext,
): string {
  const formatter = displayType
    ? DISPLAY_TYPE_FORMATTERS[displayType]
    : undefined;

  return formatter ? formatter(context) : context.formattedValue;
}

export function AccordionDisplayValue({
  selectedValue,
  displayColumnName,
  tableId,
  isSubstructure,
  displayedValueAttributeId,
  dataType,
  unitSymbol,
  culture,
  displayType,
  path,
}: AccordionDisplayValueProps): JSX.Element {
  if (displayColumnName && tableId) {
    return (
      <DisplayTableValueComponent
        selectedValue={selectedValue}
        displayColumnName={displayColumnName}
      />
    );
  }

  if (isSubstructure && displayedValueAttributeId) {
    return (
      <DisplaySubstructureValueComponent
        displayedValueAttributeId={displayedValueAttributeId}
        path={path}
      />
    );
  }

  const formattedValue = getAttributeDisplayValue({
    dataType,
    value: selectedValue?.name || selectedValue?.value,
    unitSymbol,
    culture,
  });

  return (
    <Typography color={"primary"} sx={{ fontWeight: "bold" }}>
      {getDisplayValue(displayType, {
        formattedValue,
        typeCodeFragment: selectedValue?.typeCodeFragment ?? "",
      })}
    </Typography>
  );
}
